import React, { useState } from 'react';
import {
    List,
    Datagrid,
    DateField,
    TextField,
    FunctionField,
    Filter,
    TextInput,
    SelectInput,
    DateInput,
    TopToolbar,
    useRefresh,
    useNotify,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Button,
    Chip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Person,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';
import { TopUpSummaryCards } from './TopUpSummaryCards';
import { TruncatedId } from './TruncatedId';
import { TopUpViewToggle } from './TopUpViewToggle';

const StatusChip = ({ status }: { status: string }) => {
    const translate = useTranslate();
    const color: any = (() => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'failed':
                return 'error';
            case 'cancelled':
                return 'default';
            case 'refunded':
                return 'warning';
            default:
                return 'default';
        }
    })();
    return (
        <Chip
            label={translate(`resources.app-topups.statuses.${status}`, { _: status })}
            color={color}
            size="small"
        />
    );
};

const TopUpsFilter = (props: any) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.app-topups.filters.search', { _: 'Search (email / phone / name)' })}
                source="search"
                variant="outlined"
                size="small"
                alwaysOn
                resettable
                helperText={false}
                InputLabelProps={{ shrink: true }}
            />
            <SelectInput
                label={translate('resources.app-topups.filters.status', { _: 'Status' })}
                source="status"
                variant="outlined"
                size="small"
                alwaysOn
                helperText={false}
                emptyText={translate('resources.app-topups.filters.statusAll', { _: 'All' })}
                InputLabelProps={{ shrink: true }}
                // RaSelectInput-root applies `margin: 8px 0 4px` as a shorthand;
                // the 4px bottom shifts the centered flex item up by ~4px relative
                // to the other filter inputs. Reset it here.
                sx={{ marginBottom: '0 !important' }}
                choices={[
                    { id: 'pending', name: translate('resources.app-topups.statuses.pending', { _: 'Pending' }) },
                    { id: 'completed', name: translate('resources.app-topups.statuses.completed', { _: 'Completed' }) },
                    { id: 'failed', name: translate('resources.app-topups.statuses.failed', { _: 'Failed' }) },
                    { id: 'refunded', name: translate('resources.app-topups.statuses.refunded', { _: 'Refunded' }) },
                    { id: 'cancelled', name: translate('resources.app-topups.statuses.cancelled', { _: 'Cancelled' }) },
                ]}
            />
            <DateInput
                label={translate('resources.app-topups.filters.fromDate', { _: 'From' })}
                source="from_date"
                variant="outlined"
                size="small"
                alwaysOn
                helperText={false}
                InputLabelProps={{ shrink: true }}
            />
            <DateInput
                label={translate('resources.app-topups.filters.toDate', { _: 'To' })}
                source="to_date"
                variant="outlined"
                size="small"
                alwaysOn
                helperText={false}
                InputLabelProps={{ shrink: true }}
            />
        </Filter>
    );
};

// Date-range preset buttons. Uses `useListContext` so it works anywhere inside <List>.
const DateRangePresets: React.FC<{ dense?: boolean }> = ({ dense }) => {
    const { filterValues, setFilters, displayedFilters } = useListContext();
    const translate = useTranslate();

    const applyPreset = (preset: 'today' | 'week' | 'month') => {
        const now = new Date();
        const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        if (preset === 'week') {
            const daysFromMon = (now.getUTCDay() + 6) % 7;
            from.setUTCDate(from.getUTCDate() - daysFromMon);
        } else if (preset === 'month') {
            from.setUTCDate(1);
        }
        setFilters(
            { ...(filterValues || {}), from_date: from.toISOString().slice(0, 10), to_date: undefined },
            displayedFilters
        );
    };

    return (
        <Box sx={{ display: 'inline-flex', gap: dense ? 0.5 : 1, alignItems: 'center' }}>
            <Button size="small" variant="outlined" onClick={() => applyPreset('today')}>
                {translate('resources.app-topups.filters.today', { _: 'Today' })}
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyPreset('week')}>
                {translate('resources.app-topups.filters.thisWeek', { _: 'This week' })}
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyPreset('month')}>
                {translate('resources.app-topups.filters.thisMonth', { _: 'This month' })}
            </Button>
        </Box>
    );
};

const exportTopUps = async (notify: ReturnType<typeof useNotify>, translate: ReturnType<typeof useTranslate>) => {
    try {
        const token = await tokenManager.getAccessToken();
        const resp = await fetch(
            `${API_CONFIG.baseURL}/api/backend/app-topups/export`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: 'include',
            }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `app-topups-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        notify(translate('resources.app-topups.actions.exportSuccess', { _: 'Export downloaded' }), { type: 'success' });
    } catch (error) {
        console.error('[AppTopUpsList] Export failed:', error);
        notify(translate('resources.app-topups.actions.exportFailed', { _: 'Export failed' }), { type: 'error' });
    }
};

const DesktopListActions = () => {
    const refresh = useRefresh();
    const notify = useNotify();
    const translate = useTranslate();
    return (
        <TopToolbar sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <DateRangePresets />
            <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                size="small"
            >
                {translate('resources.app-topups.actions.refresh', { _: 'Refresh' })}
            </Button>
            <Button
                startIcon={<DownloadIcon />}
                onClick={() => exportTopUps(notify, translate)}
                variant="contained"
                size="small"
            >
                {translate('resources.app-topups.actions.export', { _: 'Export' })}
            </Button>
        </TopToolbar>
    );
};

const MobileListActions = () => {
    const refresh = useRefresh();
    const notify = useNotify();
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%', p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={refresh}
                    size="small"
                    variant="outlined"
                >
                    {translate('resources.app-topups.actions.refresh', { _: 'Refresh' })}
                </Button>
                <Button
                    startIcon={<DownloadIcon />}
                    onClick={() => exportTopUps(notify, translate)}
                    size="small"
                    variant="contained"
                >
                    {translate('resources.app-topups.actions.export', { _: 'Export' })}
                </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 0.5, flexWrap: 'wrap' }}>
                <DateRangePresets dense />
            </Box>
        </Box>
    );
};

const MobileTopUpsList: React.FC = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        sx={{
                            py: 1.5,
                            px: 2,
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/app-topups/${record.id}/show`)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 700, color: 'success.main' }}>
                                + HKD {Number(record.amount).toFixed(2)}
                            </Typography>
                            <StatusChip status={record.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                            {record.user?.name && (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <Person sx={{ fontSize: 14, color: '#999' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {record.user.name}
                                        {record.user?.phone ? ` · ${record.user.phone}` : ''}
                                    </Typography>
                                </Box>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                {record.timestamp ? new Date(record.timestamp).toLocaleString() : ''}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const AppTopUpsList: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [summaryRefreshKey] = useState(0);

    return (
        <>
            <TopUpViewToggle active="successful" />
            <TopUpSummaryCards refreshKey={summaryRefreshKey} />
            <List
                resource="app-topups"
                filters={<TopUpsFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                perPage={25}
                sort={{ field: 'timestamp', order: 'DESC' }}
                title={translate('resources.app-topups.name', { smart_count: 2, _: 'Top-Ups' })}
                empty={false}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileTopUpsList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick={(id) => `/app-topups/${id}/show`}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row:hover': {
                                backgroundColor: '#F0F7FF',
                            },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.app-topups.fields.id', { _: 'ID' })}
                            render={(record: any) => <TruncatedId id={record.id} />}
                        />
                        <DateField
                            source="timestamp"
                            label={translate('resources.app-topups.fields.timestamp', { _: 'Date' })}
                            showTime
                        />
                        <FunctionField
                            label={translate('resources.app-topups.fields.user', { _: 'User' })}
                            render={(record: any) => (
                                <Box>
                                    <Typography variant="body2">{record.user?.name}</Typography>
                                    {record.user?.email && (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {record.user.email}
                                        </Typography>
                                    )}
                                    {record.user?.phone && (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {record.user.phone}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />
                        <FunctionField
                            label={translate('resources.app-topups.fields.amount', { _: 'Amount' })}
                            render={(record: any) => (
                                <Typography variant="body2" color="success.main" fontWeight="bold">
                                    + HKD {Number(record.amount).toFixed(2)}
                                </Typography>
                            )}
                        />
                        <TextField
                            source="payment_method"
                            label={translate('resources.app-topups.fields.paymentMethod', { _: 'Payment method' })}
                        />
                        <FunctionField
                            label={translate('resources.app-topups.fields.status', { _: 'Status' })}
                            render={(record: any) => <StatusChip status={record.status} />}
                        />
                    </Datagrid>
                )}
            </List>
        </>
    );
};

export default AppTopUpsList;
